import { useCallback, useEffect, useState } from "react";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { buildDataState, buildLoadingState } from "../../../utils/StateUtils";
import { useThor } from "../context/ThorContext";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import Button from "../../common/elements/Button";
import { serverErrMsg } from "../utils/validators";

const Field = ({ label, inputId, children }) => (
    <div className="field">
        <label className="label is-small" htmlFor={inputId}>{label}</label>
        <div className="control">{children}</div>
    </div>
);

const BoolField = ({ label, value, onChange, disabled, help }) => (
    <div className="field">
        <label className="label is-small">{label}{help && <HelpTooltip text={help} />}</label>
        {disabled ? (
            <p className="is-size-7 has-text-weight-semibold" style={{minHeight: '2em', paddingTop: '0.3em'}}>
                {value === true ? 'Yes' : value === false ? 'No' : <span className="has-text-grey">—</span>}
            </p>
        ) : (
            <div className="buttons has-addons">
                <button type="button"
                        className={`button is-small ${value === true ? 'is-success' : 'is-light has-text-grey'}`}
                        aria-pressed={value === true}
                        onClick={() => onChange(true)}>Yes</button>
                <button type="button"
                        className={`button is-small ${value === false ? 'is-dark' : 'is-light has-text-grey'}`}
                        aria-pressed={value === false}
                        onClick={() => onChange(false)}>No</button>
            </div>
        )}
    </div>
);

const OutcomeTab = ({ experimentKey, experimentState }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast } = useThor();

    const [resultState, setResultState] = useState(buildLoadingState(null));
    const [isSaving, setIsSaving] = useState(false);
    const [form, setForm] = useState({
        released: false, rolloutCriteriaMet: false,
        reviewedByAnalytics: false, adhocAnalysis: false,
        adhocAnalysisLink: '', conclusions: '',
    });

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const fetchResult = useCallback(() => {
        setResultState(buildLoadingState(null));
        execute('GET', thor_api_urls.experimentResult(experimentKey))
            .then(data => {
                setResultState(buildDataState(data));
                if (data) setForm({
                    released: data.released ?? false,
                    rolloutCriteriaMet: data.rolloutCriteriaMet ?? false,
                    reviewedByAnalytics: data.reviewedByAnalytics ?? false,
                    adhocAnalysis: data.adhocAnalysis ?? false,
                    adhocAnalysisLink: data.adhocAnalysisLink ?? '',
                    conclusions: data.conclusions ?? '',
                });
            })
            .catch(() => {
                // 404 = no result yet, that's fine
                setResultState(buildDataState(null));
            });
    }, [execute, experimentKey]);

    useEffect(() => { fetchResult(); }, [fetchResult]);

    const handleSave = () => {
        setIsSaving(true);
        execute('POST', thor_api_urls.experimentResult(experimentKey), form)
            .then(() => { addToast('Outcome saved.', 'success'); fetchResult(); })
            .catch(err => addToast(serverErrMsg(err, 'Failed to save outcome.'), 'danger'))
            .finally(() => setIsSaving(false));
    };

    return (
        <StateHandler showLoader={resultState.loading} showError={false}
                      loadingScreen={<SmallLoadingSpinner />}>
            <div className="level mb-4">
                <div className="level-left"><p className="subtitle is-5 mb-0">Outcome</p></div>
                <div className="level-right">
                    <Button className="is-link is-small" onClick={handleSave} disabled={!editMode || isSaving}>
                        {isSaving ? 'Saving…' : 'Save Outcome'}
                    </Button>
                </div>
            </div>

            {experimentState === 'ended' && (
                <div className="notification is-warning is-light mb-4">
                    <strong>Action required:</strong> Fill in the Outcome below before the experiment can be moved to Clean Up.
                </div>
            )}
            {!['ended', 'cleanup', 'finished'].includes(experimentState) && (
                <div className="notification is-light has-text-grey is-size-7 mb-4">
                    Outcome is recorded after the experiment ends. You can pre-fill it now, but it is required before cleanup.
                </div>
            )}

            <div className="columns is-multiline">
                <div className="column is-half">
                    <BoolField label="Released" value={form.released} onChange={v => set('released', v)}
                               disabled={!editMode} help={HELP.outcome.released} />
                </div>
                <div className="column is-half">
                    <BoolField label="Rollout Criteria Met" value={form.rolloutCriteriaMet}
                               onChange={v => set('rolloutCriteriaMet', v)} disabled={!editMode}
                               help={HELP.outcome.rolloutCriteriaMet} />
                </div>
                <div className="column is-half">
                    <BoolField label="Reviewed by Analytics" value={form.reviewedByAnalytics}
                               onChange={v => set('reviewedByAnalytics', v)} disabled={!editMode}
                               help={HELP.outcome.reviewedByAnalytics} />
                </div>
                <div className="column is-half">
                    <BoolField label="Ad-hoc Analysis" value={form.adhocAnalysis}
                               onChange={v => set('adhocAnalysis', v)} disabled={!editMode}
                               help={HELP.outcome.adhocAnalysis} />
                </div>
                <div className="column is-full">
                    <Field label="Ad-hoc Analysis Link" inputId="outcome-adhoc-link">
                        <input id="outcome-adhoc-link" className="input is-small" type="text"
                               value={form.adhocAnalysisLink} disabled={!editMode}
                               onChange={e => set('adhocAnalysisLink', e.target.value)}
                               placeholder="https://…" />
                    </Field>
                </div>
                <div className="column is-full">
                    <Field label="Conclusions" inputId="outcome-conclusions">
                        <textarea id="outcome-conclusions" className="textarea is-small" rows={4}
                                  value={form.conclusions} disabled={!editMode}
                                  onChange={e => set('conclusions', e.target.value)}
                                  placeholder="Summarise the result and the rollout decision…" />
                    </Field>
                </div>
            </div>
        </StateHandler>
    );
};

export default OutcomeTab;
